/* eslint-disable react-hooks/exhaustive-deps */
import { Search, Pencil, Plus, RotateCw } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import 'react-bootstrap-typeahead/css/Typeahead.css'
import Select from 'react-select'
import { useDebounce } from 'use-debounce'
import Loader from '../../../components/atoms/Loader'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import ModalAddStagingArea from '../../../components/modal/stagingarea/ModalAddStagingArea'
import ModalUpdateStagingArea from '../../../components/modal/stagingarea/ModalUpdateStagingArea'
import api from '../../../config/AxiosInstance'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  backgroundColor: '#f8f9fa',
  color: '#333333',
  fontWeight: '700',
  borderBottom: '1px solid #e9ecef',
  textTransform: 'uppercase',
  fontSize: '0.85rem'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const FormControlStyle = {
  border: '1px solid #e9ecef',
  borderRadius: '8px',
  padding: '0.6rem 1rem',
  fontSize: '0.9rem',
  backgroundColor: '#ffffff',
  transition: 'all 0.2s',
  height: '45px'
}

const SelectStyles = {
  control: (base, state) => ({
    ...base,
    height: 45,
    minHeight: 45,
    borderRadius: '10px',
    borderColor: state.isFocused ? 'var(--primary-orange)' : '#e9ecef',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(248, 157, 29, 0.1)' : 'none',
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    fontSize: '0.9rem'
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    height: '45px',
    padding: '0 12px'
  }),
  input: (provided, state) => ({
    ...provided,
    margin: '0px'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#495057'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--primary-orange)'
      : state.isFocused
        ? '#fff5e6'
        : null,
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer'
  })
}

const initialMeta = {
  per_page: 10,
  current_page: 1,
  total_row: 0,
  total_page: 0
}

const MasterStaging = () => {
  const [showModalAdd, setShowModalAdd] = useState(false)
  const [showModalUpdate, setShowModalUpdate] = useState(false)
  const [loading, setLoading] = useState(false)
  const [stagings, setStagings] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [editId, setEditId] = useState('')
  const [meta, setMeta] = useState(initialMeta)

  const [filter, setFilter] = useState({
    warehouseId: null,
    search: ''
  })
  const [debouncedSearch] = useDebounce(filter.search, 500)

  const prevSearch = useRef(debouncedSearch)
  const prevWarehouse = useRef(filter.warehouseId)

  const abortControllerRef = useRef(null)

  useEffect(() => {
    const searchChanged = prevSearch.current !== debouncedSearch
    const warehouseChanged = prevWarehouse.current !== filter.warehouseId

    if (!searchChanged && !warehouseChanged) {
      return
    }

    prevSearch.current = debouncedSearch
    prevWarehouse.current = filter.warehouseId

    if (meta.current_page !== 1) {
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    } else {
      fetchStagings()
    }
  }, [debouncedSearch, filter.warehouseId])

  useEffect(() => {
    fetchStagings()
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [meta.current_page, meta.per_page])

  const fetchWarehouse = async () => {
    try {
      const params = {
        is_parent: 1,
        forUser: 1
      }
      const response = await api.get('/master/warehouses/all', { params })
      const { data } = response.data
      const formatted = data.map((d) => ({
        label: d.warehouse_name,
        value: d.id
      }))
      setWarehouses(formatted)
    } catch (err) {
      // ignore
    }
  }

  const fetchStagings = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    setLoading(true)
    try {
      const params = new URLSearchParams({
        per_page: meta.per_page,
        page: meta.current_page
      })
      if (filter.warehouseId?.value) {
        params.append('warehouse_id', filter.warehouseId.value)
      }
      if (debouncedSearch) {
        params.append('q', debouncedSearch)
      }
      const res = await api.get(`master/stagings?${params.toString()}`, {
        signal
      })

      if (res.data.success) {
        const { metadata, data } = res.data
        setStagings(data)
        setMeta(metadata)
      }
    } catch (err) {
      if (
        err.name !== 'CanceledError' &&
        err.message !== 'canceled' &&
        err.code !== 'ERR_CANCELED'
      ) {
        setLoading(false)
        setStagings([])
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false)
      }
    }
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      current_page: data.selected + 1
    }))
    window.scrollTo(0, 0)
  }

  const handleEdit = (id) => {
    setShowModalUpdate(true)
    setEditId(id)
  }

  const resetFilters = () => {
    setFilter({
      warehouseId: null,
      search: ''
    })
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  useEffect(() => {
    fetchWarehouse()
  }, [])

  return (
    <div className="container-fluid px-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Staging Area</h3>
        </Col>
        <Col className="text-end">
          <Button
            className="btn btn-primary-orange shadow-sm rounded px-4"
            disabled={!filter.warehouseId}
            onClick={() => setShowModalAdd(true)}
          >
            <Plus size={16} className="me-2" />
            Add Staging
          </Button>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={5}>
            <Form.Group>
              <Form.Label style={labelStyle}>Warehouse</Form.Label>
              <Select
                name="warehouse"
                value={filter.warehouseId}
                onChange={(val) => setFilter({ ...filter, warehouseId: val })}
                options={warehouses}
                styles={SelectStyles}
                placeholder="Select Warehouse"
                isClearable
                isSearchable
              />
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group>
              <Form.Label style={labelStyle}>Search Name</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#ffffff',
                    borderRight: 'none',
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                    borderColor: '#e9ecef'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Staging Area..."
                  value={filter.search}
                  onChange={(e) =>
                    setFilter({ ...filter, search: e.target.value })
                  }
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: '0',
                    borderBottomLeftRadius: '0'
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <Button
              variant="light"
              className="w-100 rounded shadow-sm"
              style={{
                height: '45px',
                borderColor: '#e9ecef',
                color: '#6c757d',
                fontWeight: '600'
              }}
              onClick={resetFilters}
            >
              <RotateCw size={16} className="me-2" />
              Reset
            </Button>
          </Col>
        </Row>
      </Card>

      <div className="row">
        <StagingTable
          stagings={stagings}
          loading={loading}
          onHandleEdit={handleEdit}
          meta={meta}
          handleOnChangeMeta={handleOnChangeMeta}
          handlePageChange={handlePageChange}
        />
      </div>

      <ModalAddStagingArea
        show={showModalAdd}
        onHide={() => setShowModalAdd(false)}
        data={filter.warehouseId?.value}
        refreshData={fetchStagings}
      />

      <ModalUpdateStagingArea
        show={showModalUpdate}
        onHide={() => {
          setShowModalUpdate(false)
        }}
        id={editId}
        refreshData={fetchStagings}
      />
    </div>
  )
}

const StagingTable = ({
  stagings,
  loading,
  onHandleEdit,
  meta,
  handleOnChangeMeta,
  handlePageChange
}) => {
  return (
    <Col xs={12}>
      <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
        <Table hover responsive className="mb-0 b-table-frameless">
          <thead style={tableHeaderStyle}>
            <tr>
              <th className="p-2 align-middle ps-4">Name</th>
              <th
                className="p-2 align-middle text-end"
                style={{ width: '15%' }}
              >
                Total Block
              </th>
              <th
                className="p-2 align-middle text-center pe-4"
                style={{ width: '10%' }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="text-center p-5">
                  <Loader />
                </td>
              </tr>
            ) : stagings.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center p-5 text-muted">
                  No data available
                </td>
              </tr>
            ) : (
              stagings?.map((staging, index) => (
                <tr key={index}>
                  <td className="p-2 align-middle ps-4 text-dark fw-medium">
                    {staging.name}
                  </td>
                  <td
                    className="p-2 align-middle text-end text-muted"
                    style={{ width: '15%' }}
                  >
                    {staging.block_total}
                  </td>
                  <td
                    className="p-2 align-middle text-center pe-4"
                    style={{ width: '10%' }}
                  >
                    <TableAction>
                      <button
                        className="btn btn-light shadow-sm rounded d-flex align-items-center justify-content-center p-0"
                        style={{ width: '32px', height: '32px' }}
                        onClick={() => onHandleEdit(staging.id)}
                      >
                        <Pencil size={13} className="text-primary" />
                      </button>
                    </TableAction>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        {stagings.length > 0 && (
          <div className="p-3 border-top bg-white">
            <TablePagination
              meta={meta}
              handleOnChangeMeta={handleOnChangeMeta}
              handlePageChange={handlePageChange}
            />
          </div>
        )}
      </Card>
    </Col>
  )
}

export default MasterStaging
